import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAgentBridge } from "../../app/AgentBridge";
import { companySlugFromName, defaultSlugFromDate, deriveSlugFromText, deriveSlugFromUrl } from "../../shared/slug";
import { ImportView } from "../../shared/ImportView";
import { Step1Inputs } from "./Step1Inputs";
import { Step2Review } from "./Step2Review";
import { Step3Questionnaire } from "./Step3Questionnaire";
import { Step4Report } from "./Step4Report";
import { StepIndicator } from "./StepIndicator";
import {
  clearPersistedBafaState,
  loadPersistedBafaState,
  persistBafaState,
  type BafaState,
  type Form1State,
  type Form2State,
} from "./state";
import { buildStep1Prompt, buildStep2Prompt, buildStep3Prompt } from "./prompts";
import { applyBafaPrefill } from "./prefill";
import { downloadGeneratedFile, fetchCompanyInfo, fetchQuestionnaire, fetchReport, type Questionnaire } from "./api";

export function BafaWizard() {
  const { send, subscribe } = useAgentBridge();
  const [state, setState] = useState<BafaState>(loadPersistedBafaState);
  const [prefillBanner, setPrefillBanner] = useState<string | null>(null);
  // Pending step = the wizard step that just submitted to the agent and is
  // waiting for `done`. Mirrors `pendingWizardStep` in the legacy app.
  const pendingStepRef = useRef<1 | 2 | 3 | null>(null);
  const [busy, setBusy] = useState<{ 1: boolean; 2: boolean; 3: boolean }>({ 1: false, 2: false, 3: false });
  const [questionnaire, setQuestionnaire] = useState<Questionnaire | null>(null);
  const [questionnairePdfDownloading, setQuestionnairePdfDownloading] = useState(false);
  const [reportContent, setReportContent] = useState<string>("");
  const [hint, setHint] = useState<string | null>(null);

  useEffect(() => {
    persistBafaState(state);
  }, [state]);

  const updateState = useCallback((patch: Partial<BafaState>) => {
    setState((prev) => {
      const next = { ...prev, ...patch };
      if (next.step > next.maxReachedStep) next.maxReachedStep = next.step;
      return next;
    });
  }, []);

  const setStep = useCallback(
    (step: BafaState["step"]) => updateState({ phase: "wizard", step }),
    [updateState],
  );

  const skipImport = useCallback(() => {
    setState((prev) => ({ ...prev, phase: "wizard" }));
  }, []);

  const jumpToImport = useCallback(() => {
    setState((prev) => ({ ...prev, phase: "import" }));
  }, []);

  function patchForm1(patch: Partial<Form1State>) {
    setState((prev) => ({ ...prev, form1: { ...prev.form1, ...patch } }));
  }
  function patchForm2(patch: Partial<Form2State>) {
    setState((prev) => ({ ...prev, form2: { ...prev.form2, ...patch } }));
  }

  // ── Step transitions ────────────────────────────────────────────────────
  const submitStep1 = useCallback(() => {
    const { form1 } = state;
    if (!form1.reportType) return;
    const slugFromUrl = form1.websiteUrl
      ? deriveSlugFromUrl(form1.websiteUrl)
      : deriveSlugFromText(form1.kernproblem) ?? defaultSlugFromDate();
    pendingStepRef.current = 1;
    setBusy((b) => ({ ...b, 1: true }));
    setState((prev) => ({ ...prev, slugFromUrl }));
    const prompt = buildStep1Prompt(form1, slugFromUrl);
    if (!send({ type: "message", content: prompt, mode: "bafa" })) {
      setBusy((b) => ({ ...b, 1: false }));
      pendingStepRef.current = null;
      setHint("Nicht mit dem Agenten verbunden.");
    }
  }, [state, send]);

  const submitStep2 = useCallback(() => {
    const { form1, form2, slugFromUrl } = state;
    if (!form2.firmenname.trim()) return;
    const currentCompanySlug = companySlugFromName(form2.firmenname);
    pendingStepRef.current = 2;
    setBusy((b) => ({ ...b, 2: true }));
    setState((prev) => ({ ...prev, currentCompanySlug }));
    const prompt = buildStep2Prompt(form1, form2, currentCompanySlug, slugFromUrl);
    if (!send({ type: "message", content: prompt, mode: "bafa" })) {
      setBusy((b) => ({ ...b, 2: false }));
      pendingStepRef.current = null;
      setHint("Nicht mit dem Agenten verbunden.");
    }
  }, [state, send]);

  const generateReport = useCallback(() => {
    const { currentCompanySlug } = state;
    if (!currentCompanySlug) return;
    pendingStepRef.current = 3;
    setBusy((b) => ({ ...b, 3: true }));
    const prompt = buildStep3Prompt(currentCompanySlug);
    if (!send({ type: "message", content: prompt, mode: "bafa" })) {
      setBusy((b) => ({ ...b, 3: false }));
      pendingStepRef.current = null;
      setHint("Nicht mit dem Agenten verbunden.");
    }
  }, [state, send]);

  const resetChat = useCallback(() => {
    clearPersistedBafaState();
    setState(loadPersistedBafaState());
    setQuestionnaire(null);
    setReportContent("");
    setHint(null);
  }, []);

  // ── React to agent server events ────────────────────────────────────────
  const slugCandidates = useMemo(
    () => [state.currentCompanySlug, state.slugFromUrl],
    [state.currentCompanySlug, state.slugFromUrl],
  );

  const handleAgentDone = useCallback(async () => {
    const pending = pendingStepRef.current;

    if (pending === 1 && state.slugFromUrl) {
      const { data } = await fetchCompanyInfo([state.slugFromUrl, state.currentCompanySlug]);
      if (data) {
        setState((prev) => ({
          ...prev,
          form2: {
            firmenname: data.firmenname != null ? String(data.firmenname) : prev.form2.firmenname,
            branche: data.branche != null ? String(data.branche) : prev.form2.branche,
            inhaber: data.inhaber != null ? String(data.inhaber) : prev.form2.inhaber,
            standort: data.standort != null ? String(data.standort) : prev.form2.standort,
          },
          step: 2,
          maxReachedStep: 2,
        }));
      } else {
        setHint("company_info.json nicht gefunden. Agent hat die Datei möglicherweise nicht geschrieben.");
      }
      setBusy((b) => ({ ...b, 1: false }));
      pendingStepRef.current = null;
      return;
    }

    if (pending === 2 && (state.currentCompanySlug || state.slugFromUrl)) {
      const { data } = await fetchQuestionnaire(slugCandidates);
      if (data) {
        setQuestionnaire(data);
        setState((prev) => ({ ...prev, step: 3, maxReachedStep: 3 > prev.maxReachedStep ? 3 : prev.maxReachedStep }));
      } else {
        setHint("questionnaire.json nicht gefunden. Agent hat den Fragebogen möglicherweise nicht geschrieben.");
      }
      setBusy((b) => ({ ...b, 2: false }));
      pendingStepRef.current = null;
      return;
    }

    if (pending === 3 && (state.currentCompanySlug || state.slugFromUrl)) {
      const { content, slug } = await fetchReport(slugCandidates);
      if (content != null) {
        setReportContent(content);
        if (slug && slug !== state.currentCompanySlug) {
          setState((prev) => ({ ...prev, currentCompanySlug: slug }));
        }
        setState((prev) => ({ ...prev, step: 4, maxReachedStep: 4 }));
      } else {
        setHint("result.md nicht gefunden. Agent hat den Bericht möglicherweise nicht zusammengesetzt.");
      }
      setBusy((b) => ({ ...b, 3: false }));
      pendingStepRef.current = null;
      return;
    }
  }, [state, slugCandidates]);

  const fileChangedTimers = useRef<Record<string, number>>({});

  const handleFileChanged = useCallback(
    (fileType: string) => {
      // Per-fileType debounce (300ms) matches the legacy app.
      const existing = fileChangedTimers.current[fileType];
      if (existing) window.clearTimeout(existing);
      fileChangedTimers.current[fileType] = window.setTimeout(() => {
        delete fileChangedTimers.current[fileType];
        void (async () => {
          if (fileType === "company_info") {
            const { data } = await fetchCompanyInfo(slugCandidates);
            if (!data) return;
            setState((prev) => ({
              ...prev,
              form2: {
                firmenname: data.firmenname != null ? String(data.firmenname) : prev.form2.firmenname,
                branche: data.branche != null ? String(data.branche) : prev.form2.branche,
                inhaber: data.inhaber != null ? String(data.inhaber) : prev.form2.inhaber,
                standort: data.standort != null ? String(data.standort) : prev.form2.standort,
              },
              maxReachedStep: prev.maxReachedStep < 2 ? 2 : prev.maxReachedStep,
            }));
            return;
          }
          if (fileType === "questionnaire") {
            const { data } = await fetchQuestionnaire(slugCandidates);
            if (data) setQuestionnaire(data);
            return;
          }
          if (fileType === "report") {
            const { content } = await fetchReport(slugCandidates, 1, 0);
            if (content != null) setReportContent(content);
            return;
          }
        })();
      }, 300);
    },
    [slugCandidates],
  );

  useEffect(() => {
    return subscribe((msg) => {
      switch (msg.type) {
        case "import_prefill_result":
          if (msg.mode === "bafa") {
            setState((prev) => applyBafaPrefill(prev, msg.data ?? {}));
            setPrefillBanner(
              "Felder wurden aus den importierten Daten vorausgefüllt. Bitte prüfen und bei Bedarf ergänzen.",
            );
          }
          break;
        case "done":
          void handleAgentDone();
          break;
        case "error":
          if (pendingStepRef.current) {
            setBusy({ 1: false, 2: false, 3: false });
            pendingStepRef.current = null;
          }
          break;
        case "file_changed":
          if (typeof msg.fileType === "string") handleFileChanged(msg.fileType);
          break;
      }
    });
  }, [subscribe, handleAgentDone, handleFileChanged]);

  // When the user lands on Step 4 with an existing slug, fetch the latest
  // markdown so the editor isn't blank after a page reload.
  useEffect(() => {
    if (state.step !== 4) return;
    if (reportContent) return;
    if (!state.currentCompanySlug && !state.slugFromUrl) return;
    void (async () => {
      const { content } = await fetchReport(slugCandidates, 1, 0);
      if (content != null) setReportContent(content);
    })();
  }, [state.step, state.currentCompanySlug, state.slugFromUrl, reportContent, slugCandidates]);

  // Re-fetch the questionnaire when navigating back to Step 3 after a reload.
  useEffect(() => {
    if (state.step !== 3) return;
    if (questionnaire) return;
    if (!state.currentCompanySlug && !state.slugFromUrl) return;
    void (async () => {
      const { data } = await fetchQuestionnaire(slugCandidates);
      if (data) setQuestionnaire(data);
    })();
  }, [state.step, state.currentCompanySlug, state.slugFromUrl, questionnaire, slugCandidates]);

  async function handleQuestionnairePdf() {
    setQuestionnairePdfDownloading(true);
    try {
      const target = state.currentCompanySlug ?? state.slugFromUrl ?? "fragebogen";
      const result = await downloadGeneratedFile(
        slugCandidates,
        "pdf",
        "questionnaire",
        `fragebogen_${target}.pdf`,
      );
      if (!result.ok) setHint("Fragebogen-PDF fehlgeschlagen: " + (result.error ?? "Unbekannter Fehler"));
    } finally {
      setQuestionnairePdfDownloading(false);
    }
  }

  return (
    <div className="bafa-wizard">
      <StepIndicator
        phase={state.phase}
        step={state.step}
        maxReachedStep={state.maxReachedStep}
        onJumpToImport={jumpToImport}
        onJumpToStep={setStep}
      />

      {prefillBanner && (
        <div className="import-prefill-banner" role="status">
          {prefillBanner}{" "}
          <button type="button" className="link-button" onClick={() => setPrefillBanner(null)}>
            Schließen
          </button>
        </div>
      )}

      {hint && (
        <div className="info-box" role="status">
          {hint} <button type="button" className="link-button" onClick={() => setHint(null)}>Schließen</button>
        </div>
      )}

      {state.phase === "import" && <ImportView mode="bafa" onSkip={skipImport} />}
      {state.phase === "wizard" && state.step === 1 && (
        <Step1Inputs
          form={state.form1}
          loading={busy[1]}
          onChange={patchForm1}
          onSubmit={submitStep1}
          onResetChat={resetChat}
        />
      )}
      {state.phase === "wizard" && state.step === 2 && (
        <Step2Review
          form={state.form2}
          loading={busy[2]}
          onChange={patchForm2}
          onSubmit={submitStep2}
        />
      )}
      {state.phase === "wizard" && state.step === 3 && (
        <Step3Questionnaire
          data={questionnaire}
          loading={busy[3]}
          slug={state.currentCompanySlug}
          fallbackSlug={state.slugFromUrl}
          onGenerateReport={generateReport}
          onPdfDownload={() => void handleQuestionnairePdf()}
          pdfDownloading={questionnairePdfDownloading}
        />
      )}
      {state.phase === "wizard" && state.step === 4 && (
        <Step4Report
          reportContent={reportContent}
          slug={state.currentCompanySlug}
          fallbackSlug={state.slugFromUrl}
        />
      )}
    </div>
  );
}
